import { redirect } from "@tanstack/react-router";
import { fetchMe, hasInbox } from "../app/auth/session.js";
import type { RouterContext } from "./router.js";

/**
 * NOT EVERY ACCOUNT HAS AN INBOX (PO, 2026-09-11). The artist/owner seat is the band, not a correspondent;
 * LABEL and BOOKING seats are real people with real reach into the band's data and deliberately no
 * conversation. Management and crew have one.
 *
 * The shell has hidden the Messages icon from the band account since 2026-08-15, and that was the whole of
 * the enforcement — a deep link, a typed URL or a browser restoring the tab opened the modal in full. **A
 * hidden affordance is a display filter wearing a permission's costume**, which this codebase has paid for
 * before.
 *
 * A pre-paint `beforeLoad`, in the paywall's shape and for its reason: redirect BEFORE the route renders, so
 * the inbox never flashes. It fails OPEN on a `/me` fetch failure — a transient 5xx must not bounce a crew
 * member out of their own messages, and the server is the real fence (`core.messageable_people` checks BOTH ends
 * against `core.has_inbox`, so an account without one has nothing to start and nobody to start it with).
 */
export async function requireInbox({
  context,
  params,
}: {
  context: RouterContext;
  params?: { threadId?: string };
}): Promise<void> {
  let me;
  try {
    me = await context.queryClient.ensureQueryData({ queryKey: ["me"], queryFn: () => fetchMe(context.api) });
  } catch {
    return;
  }
  if (!hasInbox(me)) throw redirect({ to: "/" });
  // An ORG-only person (a manager with no band seat) keeps their inbox in the roster shell. A message push or a
  // bell row names `/messages/{id}` for every recipient, so send them to the same thread there rather than into
  // an artist shell with no band (ART-259 C6).
  const hasBandSeat = me.seats?.some((s) => s.kind === "band");
  if (!hasBandSeat && me.seats?.some((s) => s.kind === "org")) {
    throw redirect({ to: params?.threadId ? `/roster/messages/${params.threadId}` : "/roster/messages" });
  }
}
